import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Table,
  Card,
  Space,
  Modal,
  Typography,
  Row,
  Col,
  Rate,
  Tag,
  Image,
} from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import {
  SearchOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { Resizable, ResizeCallbackData } from "react-resizable";
import "react-resizable/css/styles.css";
import { serviceEvaluationData } from "../utils/mockData";
import type { ServiceEvaluation } from "../utils/mockData";
import dayjs, { Dayjs } from "dayjs";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

// 可调整列宽的表头组件
interface ResizableHeaderProps {
  width: number;
  onResize: (width: number) => void;
  children: React.ReactNode;
  [key: string]: any;
}

const ResizableHeader: React.FC<ResizableHeaderProps> = ({
  width,
  onResize,
  children,
  ...restProps
}) => {
  if (!width) {
    return <th {...restProps}>{children}</th>;
  }

  return (
    <Resizable
      width={width}
      height={0}
      handle={
        <span
          className="react-resizable-handle"
          onClick={(e) => {
            e.stopPropagation();
          }}
        />
      }
      onResize={(_: React.SyntheticEvent, { size }: ResizeCallbackData) => {
        onResize(size.width);
      }}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...restProps}>{children}</th>
    </Resizable>
  );
};

// 默认列宽配置
const DEFAULT_COLUMN_WIDTHS = {
  serviceNumber: 150,
  staffId: 120,
  staffName: 100,
  staffPhone: 120,
  serviceType: 120,
  serviceStartTime: 150,
  serviceEndTime: 150,
  actualDuration: 120,
  standardDuration: 120,
  meetsStandard: 120,
  evaluatorName: 100,
  evaluatorPhone: 120,
  overallRating: 120,
  attitudeRating: 140,
  resultRating: 140,
  feedback: 200,
  feedbackImages: 150,
  evaluationDate: 150,
};

interface SearchParams {
  serviceType?: ServiceEvaluation["serviceType"];
  customerName?: string;
  customerPhone?: string;
  evaluationDateRange?: [Dayjs, Dayjs];
  ratingRange?: [number?, number?];
  staffName?: string;
  staffPhone?: string;
}

const ServiceEvaluationComponent: React.FC = () => {
  const [form] = Form.useForm<SearchParams>();
  const [loading, setLoading] = useState<boolean>(false);
  const [dataSource, setDataSource] = useState<ServiceEvaluation[]>([]);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 50,
    total: 0,
  });
  const [searchParams, setSearchParams] = useState<SearchParams>({});
  const [feedbackModalVisible, setFeedbackModalVisible] =
    useState<boolean>(false);
  const [imageModalVisible, setImageModalVisible] = useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] =
    useState<ServiceEvaluation | null>(null);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);

  // 列宽度状态管理
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(
    DEFAULT_COLUMN_WIDTHS
  );

  // 处理列宽度调整
  const handleResize = (columnKey: string) => (width: number) => {
    setColumnWidths((prev) => ({
      ...prev,
      [columnKey]: width,
    }));
  };

  // 初始化数据
  useEffect(() => {
    handleSearch();
  }, []);

  // 搜索功能
  const handleSearch = (values: SearchParams = {}) => {
    setLoading(true);
    setSearchParams(values);

    // 模拟API调用
    setTimeout(() => {
      let filteredData = [...serviceEvaluationData];

      // 根据搜索条件过滤数据
      if (values.serviceType) {
        filteredData = filteredData.filter(
          (item) => item.serviceType === values.serviceType
        );
      }
      if (values.customerName) {
        filteredData = filteredData.filter((item) =>
          item.customerName.includes(values.customerName!)
        );
      }
      if (values.customerPhone) {
        filteredData = filteredData.filter((item) =>
          item.customerPhone.includes(values.customerPhone!)
        );
      }
      if (
        values.evaluationDateRange &&
        values.evaluationDateRange.length === 2
      ) {
        const [start, end] = values.evaluationDateRange;
        filteredData = filteredData.filter((item) => {
          const evalDate = dayjs(item.evaluationDate);
          return evalDate.isAfter(start) && evalDate.isBefore(end);
        });
      }
      if (values.ratingRange) {
        const minRating = values.ratingRange[0];
        const maxRating = values.ratingRange[1];
        if (minRating !== undefined) {
          filteredData = filteredData.filter(
            (item) => item.overallRating >= minRating
          );
        }
        if (maxRating !== undefined) {
          filteredData = filteredData.filter(
            (item) => item.overallRating <= maxRating
          );
        }
      }
      if (values.staffName) {
        filteredData = filteredData.filter((item) =>
          item.staffName.includes(values.staffName!)
        );
      }
      if (values.staffPhone) {
        filteredData = filteredData.filter((item) =>
          item.staffPhone.includes(values.staffPhone!)
        );
      }

      // 按评价日期倒排序
      filteredData.sort(
        (a, b) =>
          new Date(b.evaluationDate).getTime() -
          new Date(a.evaluationDate).getTime()
      );

      const currentPage = pagination.current || 1;
      const pageSize = pagination.pageSize || 50;
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const currentPageData = filteredData.slice(startIndex, endIndex);

      setDataSource(currentPageData);
      setPagination((prev) => ({
        ...prev,
        total: filteredData.length,
      }));
      setLoading(false);
    }, 500);
  };

  // 重置搜索
  const handleReset = () => {
    form.resetFields();
    setPagination((prev) => ({ ...prev, current: 1 }));
    handleSearch();
  };

  // 分页变化
  const handleTableChange = (newPagination: TablePaginationConfig) => {
    setPagination(newPagination);
    // 重新搜索当前页数据
    handleSearch(searchParams);
  };

  // 显示反馈详情
  const showFeedbackModal = (record: ServiceEvaluation) => {
    setSelectedRecord(record);
    setFeedbackModalVisible(true);
  };

  // 显示图片
  const showImageModal = (images: string[], index: number = 0) => {
    setSelectedImages(images);
    setCurrentImageIndex(index);
    setImageModalVisible(true);
  };

  // 渲染星级评分
  const renderRating = (rating: number) => (
    <Space>
      <Rate disabled value={rating} style={{ fontSize: 14 }} />
      <Text>{rating}星</Text>
    </Space>
  );

  // 渲染反馈内容
  const renderFeedback = (feedback: string, record: ServiceEvaluation) => {
    if (feedback.length <= 50) {
      return feedback;
    }
    return (
      <span>
        {feedback.substring(0, 50)}...
        <Button
          type="link"
          size="small"
          onClick={() => showFeedbackModal(record)}
          style={{ padding: 0, marginLeft: 4 }}
        >
          查看全文
        </Button>
      </span>
    );
  };

  // 渲染反馈图片
  const renderFeedbackImages = (images: string[]) => {
    if (!images || images.length === 0) {
      return <Text type="secondary">无图片</Text>;
    }

    return (
      <Space>
        {images.slice(0, 3).map((image, index) => (
          <Image
            key={index}
            width={40}
            height={30}
            src={image}
            style={{ objectFit: "cover", cursor: "pointer" }}
            preview={false}
            onClick={() => showImageModal(images, index)}
          />
        ))}
        {images.length > 3 && (
          <Text type="secondary">+{images.length - 3}张</Text>
        )}
      </Space>
    );
  };

  const columns: ColumnsType<ServiceEvaluation> = [
    {
      title: "服务编号",
      dataIndex: "serviceNumber",
      key: "serviceNumber",
      width: columnWidths.serviceNumber,
      onHeaderCell: () => ({
        width: columnWidths.serviceNumber,
        onResize: handleResize("serviceNumber"),
      }),
    },
    {
      title: "服务人员编号",
      dataIndex: "staffId",
      key: "staffId",
      width: columnWidths.staffId,
      onHeaderCell: () => ({
        width: columnWidths.staffId,
        onResize: handleResize("staffId"),
      }),
    },
    {
      title: "服务人员姓名",
      dataIndex: "staffName",
      key: "staffName",
      width: columnWidths.staffName,
      onHeaderCell: () => ({
        width: columnWidths.staffName,
        onResize: handleResize("staffName"),
      }),
    },
    {
      title: "服务人员手机",
      dataIndex: "staffPhone",
      key: "staffPhone",
      width: columnWidths.staffPhone,
      onHeaderCell: () => ({
        width: columnWidths.staffPhone,
        onResize: handleResize("staffPhone"),
      }),
    },
    {
      title: "服务类型",
      dataIndex: "serviceType",
      key: "serviceType",
      width: columnWidths.serviceType,
      onHeaderCell: () => ({
        width: columnWidths.serviceType,
        onResize: handleResize("serviceType"),
      }),
      render: (type: ServiceEvaluation["serviceType"]) => (
        <Tag color="blue">{type}</Tag>
      ),
    },
    {
      title: "服务开始时间",
      dataIndex: "serviceStartTime",
      key: "serviceStartTime",
      width: columnWidths.serviceStartTime,
      onHeaderCell: () => ({
        width: columnWidths.serviceStartTime,
        onResize: handleResize("serviceStartTime"),
      }),
      render: (time: string) => dayjs(time).format("YYYY-MM-DD HH:mm"),
    },
    {
      title: "服务结束时间",
      dataIndex: "serviceEndTime",
      key: "serviceEndTime",
      width: columnWidths.serviceEndTime,
      onHeaderCell: () => ({
        width: columnWidths.serviceEndTime,
        onResize: handleResize("serviceEndTime"),
      }),
      render: (time: string) => dayjs(time).format("YYYY-MM-DD HH:mm"),
    },
    {
      title: "服务时长(分钟)",
      dataIndex: "actualDuration",
      key: "actualDuration",
      width: columnWidths.actualDuration,
      onHeaderCell: () => ({
        width: columnWidths.actualDuration,
        onResize: handleResize("actualDuration"),
      }),
      render: (duration: number) => `${duration}分钟`,
    },
    {
      title: "标准时长(分钟)",
      dataIndex: "standardDuration",
      key: "standardDuration",
      width: columnWidths.standardDuration,
      onHeaderCell: () => ({
        width: columnWidths.standardDuration,
        onResize: handleResize("standardDuration"),
      }),
      render: (duration: number) => `${duration}分钟`,
    },
    {
      title: "是否满足标准",
      dataIndex: "meetsStandard",
      key: "meetsStandard",
      width: columnWidths.meetsStandard,
      onHeaderCell: () => ({
        width: columnWidths.meetsStandard,
        onResize: handleResize("meetsStandard"),
      }),
      render: (meets: boolean) =>
        meets ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            满足
          </Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="error">
            不满足
          </Tag>
        ),
    },
    {
      title: "评价人姓名",
      dataIndex: "evaluatorName",
      key: "evaluatorName",
      width: columnWidths.evaluatorName,
      onHeaderCell: () => ({
        width: columnWidths.evaluatorName,
        onResize: handleResize("evaluatorName"),
      }),
    },
    {
      title: "评价人手机",
      dataIndex: "evaluatorPhone",
      key: "evaluatorPhone",
      width: columnWidths.evaluatorPhone,
      onHeaderCell: () => ({
        width: columnWidths.evaluatorPhone,
        onResize: handleResize("evaluatorPhone"),
      }),
    },
    {
      title: "综合评级",
      dataIndex: "overallRating",
      key: "overallRating",
      width: columnWidths.overallRating,
      onHeaderCell: () => ({
        width: columnWidths.overallRating,
        onResize: handleResize("overallRating"),
      }),
      render: renderRating,
    },
    {
      title: "服务态度评级",
      dataIndex: "attitudeRating",
      key: "attitudeRating",
      width: columnWidths.attitudeRating,
      onHeaderCell: () => ({
        width: columnWidths.attitudeRating,
        onResize: handleResize("attitudeRating"),
      }),
      render: renderRating,
    },
    {
      title: "工作成果评级",
      dataIndex: "resultRating",
      key: "resultRating",
      width: columnWidths.resultRating,
      onHeaderCell: () => ({
        width: columnWidths.resultRating,
        onResize: handleResize("resultRating"),
      }),
      render: renderRating,
    },
    {
      title: "评价反馈意见",
      dataIndex: "feedback",
      key: "feedback",
      width: columnWidths.feedback,
      onHeaderCell: () => ({
        width: columnWidths.feedback,
        onResize: handleResize("feedback"),
      }),
      render: (feedback: string, record) => renderFeedback(feedback, record),
    },
    {
      title: "评价反馈图片",
      dataIndex: "feedbackImages",
      key: "feedbackImages",
      width: columnWidths.feedbackImages,
      onHeaderCell: () => ({
        width: columnWidths.feedbackImages,
        onResize: handleResize("feedbackImages"),
      }),
      render: renderFeedbackImages,
    },
    {
      title: "评价时间",
      dataIndex: "evaluationDate",
      key: "evaluationDate",
      width: columnWidths.evaluationDate,
      onHeaderCell: () => ({
        width: columnWidths.evaluationDate,
        onResize: handleResize("evaluationDate"),
      }),
      render: (time: string) => dayjs(time).format("YYYY-MM-DD HH:mm"),
    },
  ];

  return (
    <div>
      {/* 搜索区域 */}
      <Card style={{ marginBottom: 16 }}>
        <Form form={form} layout="inline" onFinish={handleSearch}>
          <Row gutter={16} style={{ width: "100%" }}>
            <Col span={6}>
              <Form.Item name="serviceType" label="服务类型">
                <Select placeholder="请选择服务类型" allowClear>
                  <Option value="随访个性化">随访个性化</Option>
                  <Option value="医院陪诊">医院陪诊</Option>
                  <Option value="搬运重物">搬运重物</Option>
                  <Option value="水电维修">水电维修</Option>
                  <Option value="体检">体检</Option>
                  <Option value="跑腿">跑腿</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="customerName" label="客户姓名">
                <Input placeholder="请输入客户姓名" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="customerPhone" label="客户手机号">
                <Input placeholder="请输入客户手机号" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="evaluationDateRange" label="评价日期范围">
                <RangePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16} style={{ width: "100%", marginTop: 16 }}>
            <Col span={6}>
              <Form.Item label="评价星级">
                <Input.Group compact>
                  <Form.Item name={["ratingRange", 0]} noStyle>
                    <Select
                      placeholder="最低星级"
                      style={{ width: "45%" }}
                      allowClear
                    >
                      <Option value={1}>1星</Option>
                      <Option value={2}>2星</Option>
                      <Option value={3}>3星</Option>
                      <Option value={4}>4星</Option>
                      <Option value={5}>5星</Option>
                    </Select>
                  </Form.Item>
                  <Input
                    style={{
                      width: "10%",
                      textAlign: "center",
                      pointerEvents: "none",
                    }}
                    placeholder="~"
                    disabled
                  />
                  <Form.Item name={["ratingRange", 1]} noStyle>
                    <Select
                      placeholder="最高星级"
                      style={{ width: "45%" }}
                      allowClear
                    >
                      <Option value={1}>1星</Option>
                      <Option value={2}>2星</Option>
                      <Option value={3}>3星</Option>
                      <Option value={4}>4星</Option>
                      <Option value={5}>5星</Option>
                    </Select>
                  </Form.Item>
                </Input.Group>
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item name="staffName" label="服务人员姓名">
                <Input placeholder="请输入服务人员姓名" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="staffPhone" label="服务人员手机号">
                <Input placeholder="请输入服务人员手机号" />
              </Form.Item>
            </Col>
            <Col span={7}>
              <Space>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  htmlType="submit"
                  loading={loading}
                >
                  搜索
                </Button>
                <Button icon={<ReloadOutlined />} onClick={handleReset}>
                  重置
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* 列表区域 */}
      <Card>
        <Table
          columns={columns}
          dataSource={dataSource}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 2400 }}
          components={{
            header: {
              cell: ResizableHeader,
            },
          }}
        />
      </Card>

      {/* 反馈详情模态框 */}
      <Modal
        title="评价反馈详情"
        open={feedbackModalVisible}
        onCancel={() => setFeedbackModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedRecord && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>服务编号：</Text>
              {selectedRecord.serviceNumber}
            </div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>服务人员：</Text>
              {selectedRecord.staffName}
            </div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>服务类型：</Text>
              <Tag color="blue">{selectedRecord.serviceType}</Tag>
            </div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>评价人：</Text>
              {selectedRecord.evaluatorName}
            </div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>综合评级：</Text>
              {renderRating(selectedRecord.overallRating)}
            </div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>服务态度评级：</Text>
              {renderRating(selectedRecord.attitudeRating)}
            </div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>工作成果评级：</Text>
              {renderRating(selectedRecord.resultRating)}
            </div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>评价时间：</Text>
              {dayjs(selectedRecord.evaluationDate).format("YYYY-MM-DD HH:mm")}
            </div>
            <div>
              <Text strong>反馈意见：</Text>
              <div
                style={{
                  marginTop: 8,
                  padding: 12,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 6,
                  lineHeight: "1.6",
                }}
              >
                {selectedRecord.feedback}
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* 图片查看模态框 */}
      <Modal
        title="评价图片"
        open={imageModalVisible}
        onCancel={() => setImageModalVisible(false)}
        footer={null}
        width={800}
        centered
      >
        {selectedImages.length > 0 && (
          <div style={{ textAlign: "center" }}>
            <Image
              src={selectedImages[currentImageIndex]}
              style={{ maxWidth: "100%", maxHeight: "500px" }}
            />
            {selectedImages.length > 1 && (
              <div style={{ marginTop: 16 }}>
                <Space>
                  <Button
                    onClick={() =>
                      setCurrentImageIndex(
                        currentImageIndex > 0
                          ? currentImageIndex - 1
                          : selectedImages.length - 1
                      )
                    }
                  >
                    上一张
                  </Button>
                  <Text>
                    {currentImageIndex + 1} / {selectedImages.length}
                  </Text>
                  <Button
                    onClick={() =>
                      setCurrentImageIndex(
                        currentImageIndex < selectedImages.length - 1
                          ? currentImageIndex + 1
                          : 0
                      )
                    }
                  >
                    下一张
                  </Button>
                </Space>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 自定义样式 */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
          .react-resizable-handle {
            position: absolute;
            width: 10px;
            height: 100%;
            bottom: 0;
            right: -5px;
            cursor: col-resize;
            z-index: 1;
          }

          .react-resizable-handle:hover {
            border-right: 2px solid #1890ff;
          }

          .ant-table-thead > tr > th {
            position: relative;
          }

          .ant-table-thead > tr > th .react-resizable-handle {
            background: transparent;
          }

          .ant-table-thead > tr > th .react-resizable-handle:hover {
            background: rgba(24, 144, 255, 0.1);
          }
          .ant-form-item-label {
            min-width: 100px;
          }
        `,
        }}
      />
    </div>
  );
};

export default ServiceEvaluationComponent;
